<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>Fuso Horário</h2>
      <p>Define como os horários são exibidos para você. Não altera os horários da obra — apenas a exibição.</p>
    </div>

    <!-- Automático -->
    <button
      class="tz-option"
      :class="{ 'tz-option--active': mode === 'auto' }"
      type="button"
      @click="chooseAuto"
    >
      <span class="tz-radio" :class="{ checked: mode === 'auto' }"></span>
      <span class="tz-option-body">
        <span class="tz-option-title">Automático</span>
        <span class="tz-option-desc">
          <template v-if="ipTz">Detectado pela sua localização (IP): <strong>{{ ipTz }}</strong></template>
          <template v-else-if="ipChecked">Não foi possível detectar pelo IP — usando o fuso do dispositivo: <strong>{{ browserTz }}</strong></template>
          <template v-else>Detectando pela sua localização…</template>
        </span>
      </span>
      <span v-if="mode === 'auto'" class="tz-clock">{{ nowPreview }}</span>
    </button>

    <!-- Manual -->
    <div class="tz-option tz-option--stack" :class="{ 'tz-option--active': mode === 'manual' }">
      <button class="tz-option-head" type="button" @click="chooseManualMode">
        <span class="tz-radio" :class="{ checked: mode === 'manual' }"></span>
        <span class="tz-option-body">
          <span class="tz-option-title">Selecionar manualmente</span>
          <span class="tz-option-desc">Escolha um fuso fixo, independente de onde você estiver.</span>
        </span>
        <span v-if="mode === 'manual'" class="tz-clock">{{ nowPreview }}</span>
      </button>
      <div v-if="mode === 'manual'" class="tz-manual-area">
        <input
          v-model="filter"
          class="tz-filter"
          type="text"
          placeholder="Buscar fuso (ex: New_York, Lisbon, Sao_Paulo)..."
        />
        <select class="tz-select" :value="manualTz" size="8" @change="onSelectTz($event)">
          <option v-for="tz in filteredZones" :key="tz" :value="tz">
            {{ tz }} ({{ offsetOf(tz) }})
          </option>
        </select>
      </div>
    </div>

    <p v-if="savedMsg" class="tz-saved">{{ savedMsg }}</p>
    <p class="tz-note">O horário atual no fuso selecionado aparece ao lado da opção ativa. A preferência fica salva na sua conta.</p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { timezoneStore, listTimeZones, currentTimeInZone, zoneOffsetLabel } from '@/store/timezone'

export default defineComponent({
  name: 'ProfileFusoTab',
  setup() {
    const filter = ref('')
    const savedMsg = ref('')
    const tick = ref(0)
    let timer: number | undefined

    const zones = listTimeZones()

    const mode = computed(() => timezoneStore.state.mode)
    const manualTz = computed(() => timezoneStore.state.manualTz)
    const ipTz = computed(() => timezoneStore.state.ipTz)
    const ipChecked = computed(() => timezoneStore.state.ipChecked)
    const browserTz = computed(() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '—' } catch { return '—' }
    })

    const filteredZones = computed(() => {
      const q = filter.value.trim().toLowerCase()
      if (!q) return zones
      return zones.filter(tz => tz.toLowerCase().includes(q))
    })

    const nowPreview = computed(() => {
      void tick.value
      return currentTimeInZone(timezoneStore.effectiveTimeZone.value)
    })

    const offsetOf = (tz: string) => zoneOffsetLabel(tz)

    const flashSaved = () => {
      savedMsg.value = 'Preferência salva.'
      window.setTimeout(() => { savedMsg.value = '' }, 2500)
    }

    const chooseAuto = async () => {
      await timezoneStore.setAuto()
      flashSaved()
    }

    const chooseManualMode = async () => {
      if (mode.value === 'manual') return
      const initial = manualTz.value || timezoneStore.effectiveTimeZone.value
      await timezoneStore.setManual(initial)
      flashSaved()
    }

    const onSelectTz = async (e: Event) => {
      const tz = (e.target as HTMLSelectElement).value
      if (!tz) return
      await timezoneStore.setManual(tz)
      flashSaved()
    }

    onMounted(() => {
      timezoneStore.initFromServer()
      if (!timezoneStore.state.ipChecked) timezoneStore.detectByIp()
      timer = window.setInterval(() => { tick.value++ }, 30_000)
    })
    onBeforeUnmount(() => { if (timer) window.clearInterval(timer) })

    return {
      filter, savedMsg,
      mode, manualTz, ipTz, ipChecked, browserTz,
      filteredZones, nowPreview, offsetOf,
      chooseAuto, chooseManualMode, onSelectTz
    }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 24px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.tz-option {
  display: flex; align-items: center; gap: 12px; width: 100%;
  padding: 14px 16px; border: 1px solid var(--border); border-radius: 10px;
  background: var(--surface, #fff); margin-bottom: 12px;
  font: inherit; text-align: left; cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
  &--active { border-color: var(--accent, #0f4c81); box-shadow: 0 0 0 1px var(--accent, #0f4c81) inset; }
  &--stack { flex-direction: column; align-items: stretch; gap: 0; padding: 0; cursor: default; }
}

.tz-option-head {
  display: flex; align-items: center; gap: 12px; width: 100%;
  padding: 14px 16px; border: none; background: transparent;
  font: inherit; text-align: left; cursor: pointer;
}

.tz-radio {
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid var(--border-strong, rgba(31,30,26,.3));
  position: relative; transition: border-color .15s;
  &.checked {
    border-color: var(--accent, #0f4c81);
    &::after {
      content: ''; position: absolute; inset: 3px; border-radius: 50%;
      background: var(--accent, #0f4c81);
    }
  }
}

.tz-option-body { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.tz-option-title { font-size: 14px; font-weight: 700; color: var(--ink); }
.tz-option-desc { font-size: 12px; color: var(--ink-muted); line-height: 1.45; strong { color: var(--ink); } }

.tz-clock {
  font-size: 18px; font-weight: 700; color: var(--accent, #0f4c81);
  font-variant-numeric: tabular-nums; flex-shrink: 0;
}

.tz-manual-area {
  display: flex; flex-direction: column; gap: 8px;
  padding: 0 16px 14px 46px;
}

.tz-filter {
  padding: 8px 10px; border: 1px solid var(--border); border-radius: 8px;
  font: inherit; font-size: 13px; color: var(--ink); background: var(--surface, #fff);
  &:focus { outline: none; border-color: var(--accent, #0f4c81); }
}

.tz-select {
  width: 100%; border: 1px solid var(--border); border-radius: 8px;
  font: inherit; font-size: 13px; color: var(--ink); background: var(--surface, #fff);
  padding: 4px;
  option { padding: 5px 8px; border-radius: 5px; }
  &:focus { outline: none; border-color: var(--accent, #0f4c81); }
}

.tz-saved { font-size: 12px; font-weight: 600; color: #15803d; margin: 4px 0 0; }
.tz-note { font-size: 12px; color: var(--ink-muted); margin: 12px 0 0; line-height: 1.5; }
</style>
